import React, { memo, Fragment, useMemo, useCallback } from 'react';
import { TableColumn } from '@modules/common/TableColumn';
import { Formatter } from '@core/utils/formatter';
import { SubProjectFormType, ListItemFormType } from '@modules/proposals/AddEditProposalPage/AddEditProposalForm';
import { PricingFooter } from './PricingFooter';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { FormCheckbox } from '@core/components/form';
import './index.scss';
import { EstimateService, HTMLService } from '@business/services';
import { Collapse } from 'reactstrap';
import { R } from '@core/utils/r';

export interface SubProjectUpgradeOptionsProps {
  subProjectItems: SubProjectFormType[];
  listItemItems: ListItemFormType[];
  optionalItemsTitle: string;
  primaryColor?: string;
  canCheckProducts?: boolean;
  selectedItemTotal: number;
  listItemsFixedPriceTotal: number;
}

export const SubProjectUpgradeOptions = memo((props: SubProjectUpgradeOptionsProps) => {
  const { subProjectItems, listItemItems, primaryColor, canCheckProducts, selectedItemTotal, listItemsFixedPriceTotal } = props;
  const combinedTotal = selectedItemTotal + listItemsFixedPriceTotal;

  return (
    <Fragment>
      <h4 className="mt-2 pt-3">{props.optionalItemsTitle ? HTMLService.toReact(props.optionalItemsTitle) : null}</h4>
      <SubProjectUpgradeOptionsHeader primaryColor={primaryColor} />
      {subProjectItems.map(ctrl => (
        <UpgradeOptionItem ctrl={ctrl} canCheckProducts={canCheckProducts} primaryColor={primaryColor} />
      ))}
      {listItemItems.map(ctrl => (
        <ListItemRow key={ctrl.value.listId} ctrl={ctrl} canCheckProducts={canCheckProducts} />
      ))}
      <PricingFooter withBorder title="Upgrades Subtotal" total={combinedTotal} />
    </Fragment>
  );
});

export const UpgradeOptionItem = memo((props: { ctrl: SubProjectFormType; canCheckProducts?: boolean; primaryColor?: string }) => {
  const { ctrl, canCheckProducts, primaryColor } = props;
  const [isOpen, setIsOpen] = React.useState(false);

  const item = useNonNilObservable(ctrl.value$);

  const vccGroups = useMemo(
    () => item.groups.filter(group => group.items.filter(o => !o.weProvide && EstimateService.getMaterialPrice(o) > 0).length > 0),
    [item.groups],
  );

  const nonVccGroups = useMemo(
    () => item.groups.filter(group => group.items.filter(o => o.weProvide && EstimateService.getItemTotal(o) > 0).length > 0),
    [item.groups],
  );

  const vccItems = useCallback(groupId => {
    const group = item.groups.find(g => g.id === groupId);
    if (!group) return [];
    return group.items.filter(o => !o.weProvide && EstimateService.getMaterialPrice(o) > 0);
  }, []);

  return (
    <Fragment>
      <div className="proposal-table-header without-border">
        <TableColumn width={50} maxWidth={50}>
          <div className="d-flex">
            <a onClick={() => setIsOpen(!isOpen)} className="text-primary mb-2" style={{ color: primaryColor }}>
              <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'} font-size-22`}></i>
            </a>
            <FormCheckbox control={ctrl.controls.isChecked} disabled={!canCheckProducts} />
          </div>
        </TableColumn>
        <TableColumn>{item.description ? HTMLService.toReact(item.description) : item.title}</TableColumn>
        <TableColumn maxWidth={130}>{Formatter.formatCurrency(item.fixedPrice, 'USD')}</TableColumn>
        <TableColumn maxWidth={130}>{Formatter.formatCurrency(item.vccAmount, 'USD')}</TableColumn>
        <TableColumn style={{ marginLeft: 20 }} maxWidth={200}>
          {Formatter.formatCurrency(item.total, 'USD')}
        </TableColumn>
      </div>
      <Collapse isOpen={isOpen}>
        {nonVccGroups.map(group => (
          <div key={group.id} className="ml-4">
            <div className="proposal-table-header" style={{ color: primaryColor }}>
              <TableColumn>{group.name}</TableColumn>
              <TableColumn style={{ marginLeft: 20 }} maxWidth={200}>
                Qty
              </TableColumn>
            </div>
            {group.items
              .filter(o => !o.isCustom && o.weProvide && EstimateService.getItemTotal(o) > 0)
              .map(item => (
                <div key={item.id} className="proposal-table-header without-border">
                  <TableColumn>{item.homeOwnerDescription || item.title || item.id}</TableColumn>
                  <TableColumn style={{ marginLeft: 20 }} maxWidth={200}>
                    {R.toNumber(item.quantity) + (item.manualQty ? R.toNumber(item.manualQty) : 0)}
                  </TableColumn>
                </div>
              ))}
            {vccItems(group.id).length > 0 && (
              <Fragment>
                <div key={group.id} className="ml-4">
                  <div className="proposal-table-header" style={{ color: primaryColor }}>
                    <TableColumn>VCC's</TableColumn>
                  </div>
                  {vccItems(group.id).map(item => (
                    <div key={item.id} className="proposal-table-header without-border">
                      <TableColumn>{item.homeOwnerDescription || item.title}</TableColumn>
                    </div>
                  ))}
                </div>
              </Fragment>
            )}
          </div>
        ))}
      </Collapse>
    </Fragment>
  );
});

const ListItemRow = memo((props: { ctrl: ListItemFormType; canCheckProducts?: boolean }) => {
  const { ctrl, canCheckProducts } = props;
  const item = useNonNilObservable(ctrl.value$);

  const { fixed, vcc, total } = EstimateService.getListItemAmounts(item);

  return (
    <div className="proposal-table-header without-border">
      <TableColumn width={50} maxWidth={50}>
        <FormCheckbox control={ctrl.controls.isChecked} disabled={!canCheckProducts} />
      </TableColumn>
      <TableColumn>{item.title}</TableColumn>
      <TableColumn maxWidth={130}>{Formatter.formatCurrency(fixed, 'USD')}</TableColumn>
      <TableColumn maxWidth={130}>{Formatter.formatCurrency(vcc, 'USD')}</TableColumn>
      <TableColumn style={{ marginLeft: 20 }} maxWidth={200}>
        {Formatter.formatCurrency(total, 'USD')}
      </TableColumn>
    </div>
  );
});

export const SubProjectUpgradeOptionsHeader = memo((props: { primaryColor?: string }) => {
  return (
    <Fragment>
      <div className="proposal-table-header without-border" style={{ color: props.primaryColor }}>
        <TableColumn width={80} maxWidth={80}></TableColumn>
        <TableColumn>Description</TableColumn>
        <TableColumn maxWidth={130}>Fixed Price</TableColumn>
        <TableColumn maxWidth={130}>VCC</TableColumn>
        <TableColumn style={{ marginLeft: 20 }} maxWidth={200}>
          Total
        </TableColumn>
      </div>
    </Fragment>
  );
});
